import { useEffect, useRef } from "react";
import { toast } from "components/ToastProvider/ToastProvider";
import type { CustomToastMessage } from "components/ToastProvider/ToastProvider";

const SESSION_STORAGE_PREFIX = "session_message_";

export type SessionMessageSeverity = NonNullable<CustomToastMessage["severity"]>;

export interface UseSessionMessageParams {
  /** When false, no toast is scheduled (caller decides e.g. client loaded, billing flags, role). */
  shouldShow: boolean;
  message: { summary: string; detail?: string };
  delayMs: number;
  sessionStorageKey: string;
  severity: SessionMessageSeverity;
}

export function useSessionMessage({
  shouldShow,
  message: { summary, detail },
  delayMs,
  sessionStorageKey,
  severity,
}: UseSessionMessageParams): void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!shouldShow) return;

    const storageKey = `${SESSION_STORAGE_PREFIX}${sessionStorageKey}`;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(storageKey)) {
      return;
    }

    // Claim the key synchronously, before scheduling the toast, so concurrent mounts/remounts and
    // multiple call sites can't each slip past the getItem guard and stack duplicate toasts.
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch {
      // ignore storage errors
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const toastMessage: CustomToastMessage = {
        severity,
        sticky: true,
        summary,
        ...(detail !== undefined && detail !== null && detail !== "" && { detail }),
      };
      toast(toastMessage);
    }, delayMs);

    return () => {
      // If the toast never fired (still pending), release the claimed key so a later mount can show
      // it. Once it has fired, timeoutRef is null and the key stays claimed for the rest of the session.
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          // ignore storage errors
        }
      }
    };
  }, [shouldShow, delayMs, sessionStorageKey, severity, summary, detail]);
}
